import { useEffect, useRef, useState } from 'react';

/**
 * Local copies of uploaded images, by file id, kept while the host lists the file: the tile
 * that replaces the upload shows the copy at once, not the remote picture still loading. Released
 * when the host lets the file go; all revoked on unmount.
 */
export function useLocalPreviews() {
  const [previews, setPreviews] = useState<Record<string, string>>({});
  // the latest copies at once: a release right after a keep, before any render, still revokes
  const previewsRef = useRef<Record<string, string>>({});
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      Object.values(previewsRef.current).forEach(url =>
        URL.revokeObjectURL(url)
      );
    };
  }, []);

  return {
    previewOf: (fileId: string): string | undefined => previews[fileId],
    keep: (fileId: string, previewUrl: string) => {
      // a copy made after unmount would never be revoked
      if (!mounted.current) {
        URL.revokeObjectURL(previewUrl);
        return;
      }
      previewsRef.current = { ...previewsRef.current, [fileId]: previewUrl };
      setPreviews(previewsRef.current);
    },
    release: (fileId: string) => {
      const previewUrl = previewsRef.current[fileId];
      if (previewUrl === undefined) return;
      URL.revokeObjectURL(previewUrl);
      const { [fileId]: _, ...rest } = previewsRef.current;
      previewsRef.current = rest;
      setPreviews(rest);
    },
    /** Releases every copy whose file is not in `keep`: pins gone by a refetch or another tab. */
    releaseExcept: (keep: ReadonlySet<string>) => {
      const gone = Object.keys(previewsRef.current).filter(
        fileId => !keep.has(fileId)
      );
      gone.forEach(fileId => {
        URL.revokeObjectURL(previewsRef.current[fileId]);
      });
      if (gone.length === 0) return;
      const rest = Object.fromEntries(
        Object.entries(previewsRef.current).filter(([fileId]) =>
          keep.has(fileId)
        )
      );
      previewsRef.current = rest;
      setPreviews(rest);
    }
  };
}
